"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, set, push, onValue } from "firebase/database"
import "./stockAvailability.css"
import InfoIcon from "./infoIcon"

const gradeOptions = ["OPC 43", "OPC 53", "PPC", "PSC"]

const StockAvailability = ({ adminView = false, selectedBrand = "" }) => {
  const [depot, setDepot] = useState("")
  const [grades, setGrades] = useState([])
  const [stockStatus, setStockStatus] = useState("")
  const [areaCovered, setAreaCovered] = useState("")
  const [error, setError] = useState("")
  const [stockEntries, setStockEntries] = useState([])
  const [showPopup, setShowPopup] = useState(false)

  const supplierBrand = adminView ? selectedBrand : localStorage.getItem("supplierBrand")

  useEffect(() => {
    if (supplierBrand) {
      const db = getDatabase()
      const stockRef = ref(db, `verifyStock/${supplierBrand}`)
      onValue(stockRef, (snapshot) => {
        const data = snapshot.val()
        if (data) {
          const stockList = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
          }))
          setStockEntries(stockList)
        } else {
          setStockEntries([])
        }
      })
    } else {
      setStockEntries([])
    }
  }, [supplierBrand, adminView, selectedBrand])

  const handleGradeChange = (grade) => {
    if (grade === "All") {
      if (grades.length === gradeOptions.length) {
        setGrades([]) // uncheck all
      } else {
        setGrades([...gradeOptions]) // select all
      }
    } else {
      setGrades((prev) =>
        prev.includes(grade) ? prev.filter((g) => g !== grade) : [...prev, grade]
      )
    }
  }

  const handleSubmit = () => {
    if (!supplierBrand) {
      setError("Supplier brand is missing. Please select a brand first.")
      return
    }

    if (!depot || grades.length === 0 || !stockStatus || !areaCovered) {
      setError("Please fill all fields!")
      setTimeout(() => setError(""), 3000)
      return
    }

    const db = getDatabase()
    const stockRef = ref(db, `verifyStock/${supplierBrand}`)
    const newStockRef = push(stockRef)

    const stockData = {
      depot,
      grade: grades.join(", "),
      stockStatus,
      areaCovered,
      timestamp: new Date().toISOString(),
    }

    set(newStockRef, stockData)
      .then(() => {
        setDepot("")
        setGrades([])
        setStockStatus("")
        setAreaCovered("")
        setShowPopup(true)
      })
      .catch((error) => {
        setError(error.message)
        console.error("Error saving stock data:", error)
      })
  }

  return (
    <div className="stock-container">
      <div className="stock-header">
        <h1 className="stock-title">
          {adminView
            ? `Stock Availability ${supplierBrand ? `for ${supplierBrand}` : "(Select a brand)"}`
            : "Stock Availability"}
          <InfoIcon text="This section allows you to update and manage the availability of your products in real time. You can specify stock levels for different brands, grades, and depot locations..." />
        </h1>
      </div>

      {error && <div className="stock-error">{error}</div>}

      {!adminView || (adminView && supplierBrand) ? (
        <>
          <div className="stock-form-card">
            <div className="stock-form">
              <div className="stock-form-group">
                <label htmlFor="depot">
                  Depot/Plant <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="depot"
                  value={depot}
                  onChange={(e) => setDepot(e.target.value)}
                  placeholder="Enter Depot/Plant"
                  className="stock-input"
                />
              </div>

              <div className="stock-form-group">
                <label>
                  Grade <span className="required">*</span>
                </label>
                <div className="stock-checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={grades.length === gradeOptions.length}
                      onChange={() => handleGradeChange("All")}
                    />
                    All
                  </label>
                  {gradeOptions.map((grade) => (
                    <label key={grade}>
                      <input
                        type="checkbox"
                        checked={grades.includes(grade)}
                        onChange={() => handleGradeChange(grade)}
                      />
                      {grade}
                    </label>
                  ))}
                </div>
              </div>

              <div className="stock-form-group">
                <label htmlFor="area">
                  Area Covered <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="area"
                  value={areaCovered}
                  onChange={(e) => setAreaCovered(e.target.value)}
                  placeholder="Enter Area Covered"
                  className="stock-input"
                />
              </div>

              <div className="stock-form-group">
                <label htmlFor="status">
                  Stock Status <span className="required">*</span>
                </label>
                <select
                  id="status"
                  value={stockStatus}
                  onChange={(e) => setStockStatus(e.target.value)}
                  className="stock-select"
                >
                  <option value="">Select Status</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Out of Stock">Out of Stock</option>
                  <option value="In Transit">In Transit</option>
                </select>
              </div>

              <button onClick={handleSubmit} className="stock-submit">
                Submit
              </button>
            </div>
          </div>

          {stockEntries.length > 0 && (
            <div className="stock-entries-container">
              <h2 className="stock-section-title">Current Stock Status</h2>
              <div className="stock-table-container">
                <table className="stock-table">
                  <thead>
                    <tr>
                      <th>Depot/Plant</th>
                      <th>Grade(s)</th>
                      <th>Area Covered</th>
                      <th>Status</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.depot}</td>
                        <td>{entry.grade}</td>
                        <td>{entry.areaCovered || "-"}</td>
                        <td>
                          <span className={`status-badge ${entry.stockStatus.toLowerCase().replace(/\s+/g, "-")}`}>
                            {entry.stockStatus}
                          </span>
                        </td>
                        <td>{new Date(entry.timestamp).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="no-brand-selected">
          <p>Please select a brand from the dropdown above to manage stock availability.</p>
        </div>
      )}
      {showPopup && (
        <div className="stock-popup-overlay">
          <div className="stock-popup-content">
            <div className="stock-popup-icon">✅</div>
            <h3>Submission Successful!</h3>
            <p>Your stock availability data has been recorded.</p>
            <button className="stock-popup-close" onClick={() => setShowPopup(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default StockAvailability